import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Icon } from "../../icons/Icon";
import { Carregando, Aviso } from "../../components/Layout";
import { api, fmt, type ErroApi } from "../../lib/api";
import { PODE_ANALISAR, PODE_HOMOLOGAR, type Sessao } from "./Admin";

type Detalhe = {
  id: number; token: string; status: string; modalidade: string;
  cargo: string; componente: string | null; preRequisito: string | null;
  enviadaEm: string; motivoIndeferimento: string | null;
  analisadaPor: string | null; analisadaEm: string | null;
  candidato: Record<string, string>;
  candidatoId: number;
  candidatoEdicaoBloqueada: boolean;
  pontuacao: { experiencia: number; qualificacao: number; total: number; mesesConsiderados: number };
  memoriaCalculo: string | null;
  experiencias: Array<Record<string, unknown>>;
  qualificacoes: Array<Record<string, unknown>>;
  documentos: Array<{ id: number; tipo: string; nome: string; tamanho: number; sensivel: boolean }>;
  historico: Array<{ titulo: string; detalhe: string; status: string; quando: string; publica: boolean }>;
};

/**
 * Análise de uma inscrição.
 *
 * O indeferimento exige motivo, e o campo é obrigatório na interface porque o
 * item 12.22 do edital manda informar individualmente a razão ao candidato — e
 * porque sem motivo o recurso do item 11.1 ficaria sem objeto.
 */
export function DetalheInscricao({ sessao }: { sessao: Sessao }) {
  const { id } = useParams();
  const navegar = useNavigate();
  const [d, setD] = useState<Detalhe | null>(null);
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [agindo, setAgindo] = useState(false);
  const [bloqueando, setBloqueando] = useState(false);

  const podeAnalisar = PODE_ANALISAR.includes(sessao.perfil ?? "");
  const podeEliminar = PODE_HOMOLOGAR.includes(sessao.perfil ?? "");

  async function carregar() {
    try { setD(await api.get<Detalhe>(`/api/admin/inscricoes/${id}`)); }
    catch { setD(null); }
  }

  useEffect(() => { void carregar(); /* eslint-disable-next-line */ }, [id]);

  async function agir(acao: "em-analise" | "deferir" | "indeferir" | "eliminar") {
    if ((acao === "indeferir" || acao === "eliminar") && motivo.trim().length < 10) {
      setErro("Descreva o motivo com pelo menos 10 caracteres: ele é comunicado ao candidato.");
      return;
    }
    setAgindo(true);
    setErro(null);
    try {
      await api.post(`/api/admin/inscricoes/${id}/${acao}`, { motivo: motivo.trim(), observacao: motivo.trim() });
      setMotivo("");
      await carregar();
    } catch (e) {
      setErro((e as ErroApi).mensagem);
    } finally {
      setAgindo(false);
    }
  }

  async function alternarBloqueioEdicao() {
    if (!d) return;
    setBloqueando(true);
    setErro(null);
    try {
      await api.post(`/api/admin/candidatos/${d.candidatoId}/bloqueio-edicao`,
        { bloqueado: !d.candidatoEdicaoBloqueada });
      await carregar();
    } catch (e) {
      setErro((e as ErroApi).mensagem);
    } finally {
      setBloqueando(false);
    }
  }

  if (!d) return <Carregando texto="Carregando a inscrição" />;

  return (
    <div className="pilha pilha-6">
      <div>
        <button type="button" className="voltar" onClick={() => navegar("/admin/inscricoes")}>
          <Icon nome="esquerda" tamanho={16} />
          Voltar à fila
        </button>
        <div className="linha linha-entre linha-quebra" style={{ marginTop: "var(--space-2)" }}>
          <div>
            <h1>{d.candidato.nome}</h1>
            <p className="texto-secundario">
              <span className="mono">{d.token}</span> · {d.cargo}
              {d.componente ? ` · ${d.componente}` : ""}
            </p>
          </div>
          <span className={`situacao situacao-${d.status}`}>
            {d.status.replace("_", " ").toLowerCase()}
          </span>
        </div>
      </div>

      {d.motivoIndeferimento && (
        <Aviso tipo="erro" titulo="Motivo registrado">{d.motivoIndeferimento}</Aviso>
      )}

      <div className="grade grade-lateral grade-lateral-320">
        <div className="pilha pilha-6">
          {/* --------------------------------------------- dados pessoais */}
          <div className="painel">
            <div className="painel-cabecalho linha linha-entre">
              <h2 className="h3">Dados do candidato</h2>
              <span className="etiqueta">
                <Icon nome="cadeado" tamanho={12} />
                acesso registrado
              </span>
            </div>
            <div className="painel-corpo">
              <dl className="grade grade-2" style={{ margin: 0 }}>
                {Object.entries(d.candidato).map(([chave, valor]) => (
                  <div key={chave}>
                    <dt className="rotulo-sobre">{ROTULOS[chave] ?? chave}</dt>
                    <dd style={{ margin: 0 }}>
                      {chave === "dataNascimento" ? fmt.data(String(valor)) : String(valor)}
                    </dd>
                  </div>
                ))}
                <div>
                  <dt className="rotulo-sobre">Modalidade</dt>
                  <dd style={{ margin: 0 }}>
                    {d.modalidade === "PCD" && <span className="etiqueta etiqueta-pcd">Pessoa com Deficiência</span>}
                    {d.modalidade === "NEGRO" && <span className="etiqueta etiqueta-negro">Vaga reservada a negros</span>}
                    {d.modalidade === "AMPLA" && "Ampla concorrência"}
                  </dd>
                </div>
              </dl>

              {podeAnalisar && (
                <div className="linha linha-entre" style={{ marginTop: "var(--space-4)" }}>
                  <span className="texto-sutil">
                    {d.candidatoEdicaoBloqueada
                      ? "Edição do perfil bloqueada: o candidato não consegue alterar os dados da conta dele."
                      : "O candidato pode editar os dados da conta dele (nome, endereço, experiência, formação)."}
                  </span>
                  <button type="button" className="btn btn-sutil btn-pequeno" disabled={bloqueando}
                          onClick={alternarBloqueioEdicao}>
                    <Icon nome="cadeado" tamanho={14} />
                    {d.candidatoEdicaoBloqueada ? "Desbloquear edição" : "Bloquear edição"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ------------------------------------------------ pré-requisito */}
          {d.preRequisito && (
            <div className="painel">
              <div className="painel-cabecalho"><h2 className="h3">Pré-requisito do componente</h2></div>
              <div className="painel-corpo">
                <p className="texto-sm">{d.preRequisito}</p>
                <p className="texto-sutil" style={{ marginTop: "var(--space-2)" }}>
                  A formação usada como pré-requisito não entra na contagem de pontos.
                </p>
              </div>
            </div>
          )}

          {/* -------------------------------------------------- experiência */}
          <div className="painel">
            <div className="painel-cabecalho"><h2 className="h3">Experiência declarada</h2></div>
            {d.experiencias.length === 0 ? (
              <p className="vazio">Nenhum período declarado.</p>
            ) : (
              <div className="tabela-rolagem">
                <table className="tabela">
                  <caption className="so-leitor">Períodos de experiência declarados</caption>
                  <thead>
                    <tr>
                      <th scope="col">Item</th><th scope="col">Instituição</th>
                      <th scope="col">Cargo / função</th><th scope="col">Vínculo</th>
                      <th scope="col">Período</th><th scope="col">Comprovante</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.experiencias.map((e, i) => (
                      <tr key={i}>
                        <td><strong>{String(e.item)}</strong></td>
                        <td>{String(e.instituicao)}</td>
                        <td>{String(e.cargoFuncao)}</td>
                        <td className="texto-sutil">{ROTULO_REDE[String(e.vinculo)] ?? String(e.vinculo)}</td>
                        <td className="num">{fmt.data(String(e.inicio))} a {fmt.data(String(e.fim))}</td>
                        <td><LinkComprovante id={e.documentoId} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ------------------------------------------------ qualificação */}
          <div className="painel">
            <div className="painel-cabecalho"><h2 className="h3">Qualificação declarada</h2></div>
            {d.qualificacoes.length === 0 ? (
              <p className="vazio">Nenhum título declarado.</p>
            ) : (
              <div className="tabela-rolagem">
                <table className="tabela">
                  <caption className="so-leitor">Títulos declarados</caption>
                  <thead>
                    <tr>
                      <th scope="col">Categoria</th><th scope="col">Título</th>
                      <th scope="col">Instituição</th><th scope="col" className="col-num">CH</th>
                      <th scope="col" className="col-num">Conclusão</th>
                      <th scope="col" className="col-num">Pontos</th>
                      <th scope="col">Comprovante</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.qualificacoes.map((q, i) => (
                      <tr key={i}>
                        <td><strong>{String(q.categoria)}, {String(q.alinea)}</strong></td>
                        <td>{String(q.descricao)}</td>
                        <td>{String(q.instituicao)}</td>
                        <td className="col-num">{q.cargaHoraria ? `${q.cargaHoraria}h` : ""}</td>
                        <td className="col-num">{q.dataConclusao ? fmt.data(String(q.dataConclusao)) : ""}</td>
                        <td className="col-num"><strong>{fmt.pontos(Number(q.pontos))}</strong></td>
                        <td><LinkComprovante id={q.documentoId} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ------------------------------------------------- documentos */}
          {d.documentos.length > 0 && (
            <div className="painel">
              <div className="painel-cabecalho"><h2 className="h3">Documentos enviados</h2></div>
              <div className="painel-corpo pilha pilha-2">
                {d.documentos.map((doc) => (
                  <div key={doc.id} className="linha linha-entre">
                    <span className="linha" style={{ gap: "var(--space-2)" }}>
                      <Icon nome="arquivo" tamanho={16} />
                      {doc.nome}
                      {doc.sensivel && (
                        <span className="etiqueta">
                          <Icon nome="cadeado" tamanho={12} /> sensível
                        </span>
                      )}
                    </span>
                    <a className="btn btn-sutil btn-pequeno"
                       href={`/api/admin/inscricoes/documentos/${doc.id}`} target="_blank" rel="noreferrer">
                      <Icon nome="externo" tamanho={14} /> Abrir
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* --------------------------------------------------- histórico */}
          <div className="painel">
            <div className="painel-cabecalho"><h2 className="h3">Histórico</h2></div>
            <div className="painel-corpo">
              <ol className="timeline" style={{ listStyle: "none" }}>
                {[...d.historico].reverse().map((m, i) => (
                  <li key={i} className="timeline-item">
                    <span className="timeline-marcador" aria-hidden="true">
                      <Icon nome="circulo" tamanho={10} />
                    </span>
                    <p style={{ fontWeight: 600 }}>
                      {m.titulo}
                      {!m.publica && <span className="etiqueta" style={{ marginLeft: "var(--space-2)" }}>interna</span>}
                    </p>
                    <p className="texto-sutil num">{fmt.dataHora(m.quando)}</p>
                    {m.detalhe && <p className="texto-sm">{m.detalhe}</p>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* ------------------------------------------------ painel de ação */}
        <aside className="pilha pilha-4" style={{ position: "sticky", top: "var(--space-4)" }}>
          <div className="cartao pilha pilha-2">
            <p className="rotulo-sobre">Pontuação declarada</p>
            <div className="calculo-linha">
              <span>Experiência</span>
              <span className="num">{fmt.pontos(d.pontuacao.experiencia)}</span>
            </div>
            <p className="texto-sutil" style={{ marginTop: 0 }}>
              {d.pontuacao.mesesConsiderados} meses considerados
            </p>
            <div className="calculo-linha">
              <span>Qualificação</span>
              <span className="num">{fmt.pontos(d.pontuacao.qualificacao)}</span>
            </div>
            <div className="calculo-total">
              <span>Total</span>
              <span className="num">{fmt.pontos(d.pontuacao.total)}</span>
            </div>
          </div>

          {podeAnalisar ? (
            <div className="cartao pilha">
              <p className="rotulo-sobre">Decisão da comissão</p>

              {erro && (
                <div className="aviso aviso-erro" role="alert">
                  <Icon nome="alerta" tamanho={16} />
                  <div>{erro}</div>
                </div>
              )}

              <div className="campo">
                <label className="campo-rotulo" htmlFor="motivo">
                  Motivo / observação
                </label>
                <span className="campo-ajuda">
                  Obrigatório para indeferir ou eliminar. O texto é exibido ao candidato.
                </span>
                <textarea id="motivo" className="area-texto" style={{ minHeight: 110 }}
                          value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              </div>

              <button className="btn btn-primario btn-largo" disabled={agindo}
                      onClick={() => agir("deferir")}>
                <Icon nome="checado" tamanho={18} /> Deferir inscrição
              </button>
              <button className="btn btn-secundario btn-largo" disabled={agindo}
                      onClick={() => agir("em-analise")}>
                <Icon nome="relogio" tamanho={18} /> Marcar em análise
              </button>
              <button className="btn btn-perigo btn-largo" disabled={agindo}
                      onClick={() => agir("indeferir")}>
                <Icon nome="cancelar" tamanho={18} /> Indeferir
              </button>
              {podeEliminar && (
                <button className="btn btn-sutil btn-largo" disabled={agindo}
                        style={{ color: "var(--color-danger)" }}
                        onClick={() => agir("eliminar")}>
                  Eliminar do certame
                </button>
              )}

              <p className="texto-sutil">
                Eliminação só nas hipóteses previstas. Ausência em chamada ou
                documentação incompleta <strong>não</strong> eliminam: preservam a posição.
              </p>
            </div>
          ) : (
            <Aviso tipo="info" titulo="Somente leitura">
              O seu perfil permite consultar, mas não decidir sobre inscrições.
            </Aviso>
          )}

          <Link to={`/consulta/${d.token}`} className="btn btn-sutil btn-largo" target="_blank">
            <Icon nome="externo" tamanho={16} />
            Ver como o candidato vê
          </Link>
        </aside>
      </div>
    </div>
  );
}

const ROTULOS: Record<string, string> = {
  nome: "Nome", cpf: "CPF", dataNascimento: "Data de nascimento", rg: "RG",
  email: "E-mail", telefone: "Telefone", endereco: "Endereço",
};

const ROTULO_REDE: Record<string, string> = {
  MUNICIPAL_BG: "Rede Municipal de Baixo Guandu",
  ESTADUAL: "Rede Estadual",
  OUTRA_MUNICIPAL: "Outra Rede Municipal",
  PRIVADA: "Rede Privada",
  PUBLICO: "Órgão público",
  PRIVADO: "Iniciativa privada",
};

/** Abre o PDF anexado pelo candidato a um período ou título; vazio quando não há. */
function LinkComprovante({ id }: { id: unknown }) {
  if (typeof id !== "number") return <span className="texto-sutil">Não anexado</span>;
  return (
    <a className="btn btn-sutil btn-pequeno" href={`/api/admin/inscricoes/documentos/${id}`}
       target="_blank" rel="noreferrer">
      <Icon nome="externo" tamanho={14} /> Ver PDF
    </a>
  );
}
